/**
 * SpectrogramRenderer — JS-side STFT spectrogram into a Canvas, in ~200 ms.
 *
 * Replaces calls to FFmpeg WASM `showspectrumpic` (5-10 s per render due to
 * encode→decode→FFT→PNG→read round-trip). Matches FFmpeg output visually:
 *   showspectrumpic=s=WxH:legend=disabled:scale=log:color=intensity
 * — log-scaled frequency on Y-axis, intensity-style colormap, time on X-axis.
 *
 * Public API:
 *   await SpectrogramRenderer.render(audioBuffer, canvas, opts)
 *     audioBuffer: Web Audio AudioBuffer (any sample rate / channels — first
 *                  channel is used; FFmpeg's default behavior matches).
 *     canvas:      target HTMLCanvasElement; opts.width/height override its
 *                  CSS-sized backing store before drawing.
 *     opts:        { width=560, height=130, fftSize=1024, fmin=20, fmax=null
 *                    (=Nyquist), dbFloor=-100, dbCeil=0, color='intensity',
 *                    scale='log' }
 *                  scale:'linear' spaces the rows evenly in hertz (fmin may be 0)
 *                  and takes fmax as given: rows above the file's Nyquist stay
 *                  background. That is the view in which a codec cut-off is a
 *                  visible shelf — on the log axis the whole 10-20 kHz octave
 *                  is a tenth of the height. rowOf() gives the row of a
 *                  frequency under the same opts (for a marker line).
 *
 * Algorithm:
 *   - one FFT frame per output column (`hop = samples / width`)
 *   - Hann window on FFT_SIZE samples around column center
 *   - radix-2 FFT → magnitude → 20·log10 → clamp [dbFloor, dbCeil] → [0,1]
 *   - log-mapped frequency bin per pixel-row → intensity palette → fillRect
 *
 * Yields to UI every 32 columns via `await new Promise(r=>setTimeout(r,0))`
 * so a 4K spectrogram render doesn't block the main thread.
 *
 * Performance: 60-sec audio @ 48 kHz, 560×130 canvas → ~150-300 ms warm,
 * vs ~5-10 s for the FFmpeg path. 30-50× speedup.
 */
(function(){
    var DEFAULTS = {
        width: 560, height: 130, fftSize: 1024,
        fmin: 20, fmax: null, dbFloor: -100, dbCeil: 0,
        color: 'intensity', backgroundColor: '#000',
        scale: 'log',
    };

    // Radix-2 in-place FFT (Cooley-Tukey). re/im Float32Array length n=2^k.
    function fft(re, im) {
        var n = re.length;
        for (var i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) {
                var tr = re[i]; re[i] = re[j]; re[j] = tr;
                var ti = im[i]; im[i] = im[j]; im[j] = ti;
            }
        }
        for (var len = 2; len <= n; len <<= 1) {
            var ang = -2 * Math.PI / len;
            var wlr = Math.cos(ang), wli = Math.sin(ang);
            for (var i2 = 0; i2 < n; i2 += len) {
                var wr = 1, wi = 0;
                for (var k = 0; k < (len >> 1); k++) {
                    var a = i2 + k, b = i2 + k + (len >> 1);
                    var tre = wr * re[b] - wi * im[b];
                    var tim = wr * im[b] + wi * re[b];
                    re[b] = re[a] - tre; im[b] = im[a] - tim;
                    re[a] += tre;        im[a] += tim;
                    var nwr = wr * wlr - wi * wli;
                    wi = wr * wli + wi * wlr;
                    wr = nwr;
                }
            }
        }
    }

    // Hann window cache, keyed by FFT size (most callers use the same size).
    var _hannCache = {};
    function hann(n) {
        if (_hannCache[n]) return _hannCache[n];
        var w = new Float32Array(n);
        for (var i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (n - 1));
        return _hannCache[n] = w;
    }

    // Intensity palette — matches FFmpeg's `color=intensity` 9-stop ramp:
    // black → dark-purple → blue → green → yellow → orange → red → light-red → white-pink
    // (sourced from libavfilter/avf_showspectrum.c intensity_color_map).
    var INTENSITY_STOPS = [
        [0.000,   0,   0,   0],
        [0.130,  75,  38, 156],
        [0.250,  26, 104, 174],
        [0.385,  90, 145,  80],
        [0.495, 144, 198,  41],
        [0.620, 215, 215,  12],
        [0.740, 255, 154,   4],
        [0.860, 218,  53,   0],
        [1.000, 255,   0,   0],
    ];
    function intensityRGB(t) {
        if (t <= 0) return [0, 0, 0];
        if (t >= 1) return [255, 0, 0];
        for (var i = 1; i < INTENSITY_STOPS.length; i++) {
            var hi = INTENSITY_STOPS[i];
            if (t <= hi[0]) {
                var lo = INTENSITY_STOPS[i - 1];
                var k = (t - lo[0]) / (hi[0] - lo[0]);
                return [
                    Math.round(lo[1] + (hi[1] - lo[1]) * k),
                    Math.round(lo[2] + (hi[2] - lo[2]) * k),
                    Math.round(lo[3] + (hi[3] - lo[3]) * k),
                ];
            }
        }
        return [255, 0, 0];
    }

    // The frequency axis: what the bottom and the top row stand for. One owner
    // for the row-to-bin table and for rowOf().
    function axisOf(fmin, fmax, sampleRate, fftSize, scale) {
        var nyquist = sampleRate / 2;
        if (scale === 'linear') {
            // fmax is taken as given: an axis that ends above the file's own
            // Nyquist shows the missing band as background.
            return { linear: true, lo: Math.max(0, fmin || 0), hi: fmax || nyquist };
        }
        return {
            linear: false,
            lo: Math.max(fmin, sampleRate / fftSize), // can't resolve below 1 bin
            hi: Math.min(fmax || nyquist, nyquist),
        };
    }

    // Pre-compute the row-to-bin mapping. Y goes from top (high freq) to
    // bottom (low freq) — same as FFmpeg's showspectrumpic default. Row 0
    // = fmax, row (height-1) = fmin. Log-spaced unless scale is 'linear'.
    function makeRowToBin(height, fmin, fmax, sampleRate, fftSize, scale) {
        var ax = axisOf(fmin, fmax, sampleRate, fftSize, scale);
        var logLo = ax.linear ? 0 : Math.log(ax.lo), logHi = ax.linear ? 0 : Math.log(ax.hi);
        var rowBin = new Float32Array(height);
        for (var r = 0; r < height; r++) {
            // Top row = high freq → invert
            var t = (height - 1 - r) / (height - 1);
            var f = ax.linear ? ax.lo + (ax.hi - ax.lo) * t : Math.exp(logLo + (logHi - logLo) * t);
            rowBin[r] = f / sampleRate * fftSize;
        }
        return rowBin;
    }

    /**
     * Row (fractional, 0 = top) at which `freq` is drawn on a canvas `height`
     * rows tall under the same opts render() was given. Clamped to the canvas.
     */
    function rowOf(freq, height, sampleRate, opts) {
        opts = Object.assign({}, DEFAULTS, opts || {});
        var ax = axisOf(opts.fmin, opts.fmax, sampleRate, opts.fftSize | 0, opts.scale);
        var f = Math.max(ax.lo, Math.min(ax.hi, freq));
        var t = ax.linear
            ? (f - ax.lo) / (ax.hi - ax.lo)
            : (Math.log(f) - Math.log(ax.lo)) / (Math.log(ax.hi) - Math.log(ax.lo));
        return (height - 1) * (1 - t);
    }

    /**
     * Render audioBuffer into canvas. Returns Promise<void> (resolves after
     * the last setTimeout(0) yield, so the caller can await rendering done).
     */
    async function render(audioBuffer, canvas, opts) {
        opts = Object.assign({}, DEFAULTS, opts || {});
        var W = opts.width | 0;
        var H = opts.height | 0;
        var FFT_SIZE = opts.fftSize | 0;
        if ((FFT_SIZE & (FFT_SIZE - 1)) !== 0) {
            throw new Error('SpectrogramRenderer: fftSize must be a power of two, got ' + FFT_SIZE);
        }

        canvas.width = W;
        canvas.height = H;
        var ctx2d = canvas.getContext('2d');
        ctx2d.fillStyle = opts.backgroundColor;
        ctx2d.fillRect(0, 0, W, H);

        // Use channel 0 — for stereo this matches FFmpeg's default which mono-
        // mixes via showspectrumpic's overlap=1 single-pic mode anyway. For
        // multi-channel monitoring, callers can render per-channel separately.
        var sr = audioBuffer.sampleRate;
        var data = audioBuffer.getChannelData(0);
        var totalSamples = data.length;
        if (totalSamples < FFT_SIZE) {
            // Audio too short — render a flat black image and return.
            return;
        }

        // hop chosen so we land EXACTLY W frames across the whole signal.
        // Last frame is centered at totalSamples - FFT_SIZE/2 to avoid
        // out-of-bounds reads in the windowed copy below.
        var hop = (totalSamples - FFT_SIZE) / Math.max(1, W - 1);
        var win = hann(FFT_SIZE);
        var re = new Float32Array(FFT_SIZE);
        var im = new Float32Array(FFT_SIZE);

        var rowBin = makeRowToBin(H, opts.fmin, opts.fmax, sr, FFT_SIZE, opts.scale);
        var nBins = (FFT_SIZE >> 1) + 1;
        var dbFloor = opts.dbFloor;
        var dbRange = opts.dbCeil - opts.dbFloor;

        // ImageData strip is one column wide — putImageData per column avoids
        // a full-canvas re-paint and lets us yield mid-render without flashing.
        var stripData = ctx2d.createImageData(1, H);
        var stripPx = stripData.data;
        var YIELD_EVERY = 32;

        for (var col = 0; col < W; col++) {
            var off = Math.round(col * hop) | 0;
            for (var i = 0; i < FFT_SIZE; i++) {
                re[i] = (off + i < totalSamples ? data[off + i] : 0) * win[i];
                im[i] = 0;
            }
            fft(re, im);

            // Magnitude → log → normalised intensity for each row, write strip.
            for (var r = 0; r < H; r++) {
                var bin = rowBin[r];
                // A linear axis may end above Nyquist: the file has nothing
                // there, and the row is drawn as nothing (not as its top bin).
                if (bin > nBins - 1) {
                    var bpx = r * 4;
                    stripPx[bpx] = 0; stripPx[bpx + 1] = 0; stripPx[bpx + 2] = 0; stripPx[bpx + 3] = 255;
                    continue;
                }
                var bLo = bin | 0;
                if (bLo >= nBins - 1) bLo = nBins - 2;
                var frac = bin - bLo;
                // Linear interp between adjacent bins for smoother render.
                var mLo = Math.sqrt(re[bLo] * re[bLo] + im[bLo] * im[bLo]);
                var mHi = Math.sqrt(re[bLo + 1] * re[bLo + 1] + im[bLo + 1] * im[bLo + 1]);
                var m = mLo + (mHi - mLo) * frac;
                if (m < 1e-12) m = 1e-12;
                var db = 20 * Math.log10(m);
                var t = (db - dbFloor) / dbRange;
                if (t < 0) t = 0; else if (t > 1) t = 1;
                var rgb = intensityRGB(t);
                var px = r * 4;
                stripPx[px]     = rgb[0];
                stripPx[px + 1] = rgb[1];
                stripPx[px + 2] = rgb[2];
                stripPx[px + 3] = 255;
            }
            ctx2d.putImageData(stripData, col, 0);

            if ((col & (YIELD_EVERY - 1)) === 0 && col > 0) {
                await new Promise(function(r){ setTimeout(r, 0); });
            }
        }
    }

    /**
     * Convenience: render to a returned data URL (PNG). Useful for blobs/<img>
     * targets when callers don't want to manage the canvas themselves.
     */
    async function renderToDataURL(audioBuffer, opts) {
        var canvas = document.createElement('canvas');
        await render(audioBuffer, canvas, opts);
        return canvas.toDataURL('image/png');
    }

    window.SpectrogramRenderer = {
        render: render,
        renderToDataURL: renderToDataURL,
        rowOf: rowOf,
        // Exposed for advanced callers / tests.
        _fft: fft,
        _intensityRGB: intensityRGB,
        __isStub: false,
    };
})();
